import { useTranslation } from "react-i18next";
import { useAppTransitionStore, EXIT_MS } from "../../store/appTransitionStore";
import { appTileClass } from "../base/AppsSidebar/appTile";

// ease-out fuerte para UI (las integradas de CSS/Tailwind son débiles).
const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";

// Indicador de actividad estilo iOS: 8 barras con opacidad decreciente; el
// conjunto rota por pasos (animate-activitySpin), sólo transform.
function ActivityIndicator({ isDarkMode }: { isDarkMode: boolean }) {
  return (
    <span aria-hidden className="relative block w-6 h-6 animate-activitySpin">
      {Array.from({ length: 8 }, (_, i) => (
        <span
          key={i}
          className={`absolute left-1/2 top-0 w-[2.5px] h-[7px] -ml-[1.25px] rounded-full ${
            isDarkMode ? "bg-zinc-200" : "bg-zinc-700"
          }`}
          style={{ transform: `rotate(${i * 45}deg)`, transformOrigin: "50% 12px", opacity: 1 - i * 0.11 }}
        />
      ))}
    </span>
  );
}

// Cortina que tapa el cambio de app (tiempos y porqué en appTransitionStore):
// ícono de la app destino, "Abriendo <app>…" y un indicador de actividad que
// dice que algo está pasando. Fondo casi sólido y sin backdrop-blur (difuminar
// la pantalla completa es caro). Con "reducir movimiento" no hay escala de
// entrada (motion-safe); el indicador sigue, porque comunica progreso.
export default function AppTransitionOverlay() {
  const { t } = useTranslation("common");
  const { phase, name, icon, isDarkMode } = useAppTransitionStore();
  if (phase === "idle") return null;

  const visible = phase === "in";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed inset-0 z-[9990] flex items-center justify-center transition-opacity ${
        visible ? "opacity-100 animate-curtainFade" : "opacity-0"
      } ${isDarkMode ? "bg-zinc-950" : "bg-zinc-100"}`}
      style={{ transitionDuration: `${EXIT_MS}ms`, transitionTimingFunction: EASE_OUT }}
    >
      <div className="flex flex-col items-center gap-4 motion-safe:animate-curtainIn">
        <span className={`${appTileClass("active", isDarkMode)} w-14 h-14 rounded-2xl flex items-center justify-center text-[28px]`}>
          {icon}
        </span>
        <div className="flex items-center gap-2.5">
          <ActivityIndicator isDarkMode={isDarkMode} />
          <p className={`text-sm font-medium ${isDarkMode ? "text-zinc-200" : "text-zinc-700"}`}>
            {t("appsRail.opening", { name, defaultValue: "Abriendo {{name}}…" })}
          </p>
        </div>
      </div>
    </div>
  );
}
